import api from './api';
import { API_ROUTES } from '@/lib/constants';
import { createCrudService } from './crud.factory';
import type { Expense, ExpenseCategory, Attachment, PaginatedResponse, ApiResponse } from '@/types/table';

export const expenseService = {
  ...createCrudService<Expense>(API_ROUTES.EXPENSES),

  submit: async (id: string): Promise<ApiResponse<Expense>> => {
    const response = await api.post(`${API_ROUTES.EXPENSES}/${id}/submit`);
    return response.data;
  },

  approve: async (id: string, comment?: string): Promise<ApiResponse<Expense>> => {
    const response = await api.post(`${API_ROUTES.EXPENSES}/${id}/approve`, { comment });
    return response.data;
  },

  reject: async (id: string, comment?: string): Promise<ApiResponse<Expense>> => {
    const response = await api.post(`${API_ROUTES.EXPENSES}/${id}/reject`, { comment });
    return response.data;
  },

  reimburse: async (id: string): Promise<ApiResponse<Expense>> => {
    const response = await api.post(`${API_ROUTES.EXPENSES}/${id}/reimburse`);
    return response.data;
  },

  uploadAttachment: async (id: string, file: File): Promise<ApiResponse<Attachment>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_ROUTES.EXPENSES}/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAttachment: async (id: string, attachmentId: string) => {
    await api.delete(`${API_ROUTES.EXPENSES}/${id}/attachments/${attachmentId}`);
  },

  // Unified export — format is one of 'csv' | 'xlsx' | 'pdf'. Overrides the
  // crud factory's /export/csv (which this entity's backend no longer serves).
  exportData: async (format: string, params?: object): Promise<Blob> => {
    const response = await api.get(`${API_ROUTES.EXPENSES}/export`, { params: { ...params, format }, responseType: 'blob' });
    return response.data;
  },

  getPendingApprovals: async (): Promise<PaginatedResponse<Expense>> => {
    const response = await api.get(`${API_ROUTES.EXPENSES}/pending-approvals`);
    return response.data;
  },

  getCategories: async (): Promise<PaginatedResponse<ExpenseCategory>> => {
    const response = await api.get(API_ROUTES.EXPENSE_CATEGORIES);
    return response.data;
  },

  createCategory: async (data: Partial<ExpenseCategory>): Promise<ApiResponse<ExpenseCategory>> => {
    const response = await api.post(API_ROUTES.EXPENSE_CATEGORIES, data);
    return response.data;
  },

  updateCategory: async (id: string, data: Partial<ExpenseCategory>): Promise<ApiResponse<ExpenseCategory>> => {
    const response = await api.patch(`${API_ROUTES.EXPENSE_CATEGORIES}/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string) => {
    await api.delete(`${API_ROUTES.EXPENSE_CATEGORIES}/${id}`);
  },
};
